import { useCallback } from 'react';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { LTLQuickQuoteRequest } from 'lib/api/getLTLQuickQuotes';
import { Undef } from 'types/UtilityTypes';
import { AccessorialIntegration, TMS } from 'types/enums/Integrations';
import captureException from 'utils/captureException';

import { useQuickQuoteContext } from '../../context/QuickQuoteContext';
import { QuickQuoteInputs } from '../../types';
import {
  LTLQuoteJobOutcome,
  LTLQuoteJobPointer,
  pollLTLQuoteJob,
  runLTLQuoteJob,
} from '../../utils/runLTLQuoteJob';
import { applyMissingShiplifyLookups } from '../applyShiplifyLookups';

type UseLTLQuoteJobParams = {
  tms: Undef<TMS>;
  isThreeGActive?: boolean;
};

type RunLTLQuoteParams = {
  // Normalized form values, before the Shiplify check.
  formValues: QuickQuoteInputs;
  buildRequest: (formValues: QuickQuoteInputs) => LTLQuickQuoteRequest;
  signal: AbortSignal;
  // The values after Shiplify filled in lookups and recommended accessorials, so the form
  // can show what was sent.
  onShiplifyChecked?: (formValues: QuickQuoteInputs) => void;
  onJobStarted?: (pointer: LTLQuoteJobPointer, quoteRequestId: number) => void;
};

// Rates one LTL quote the way the Quick Quote form does: Shiplify check for Tai, then the
// LTL job (start + poll + classify). Batch LTL rates each shipment through this hook.
export function useLTLQuoteJob({
  tms,
  isThreeGActive = false,
}: UseLTLQuoteJobParams) {
  const { accessorialIntegrations, quickQuoteConfig } = useServiceFeatures();
  const { ltlAccessorials } = useQuickQuoteContext();
  const shouldCheckShiplify =
    tms === TMS.Tai &&
    (accessorialIntegrations ?? []).some(
      (integration) => integration.name === AccessorialIntegration.Shiplify
    );
  const skipAddressBookStops =
    quickQuoteConfig?.skipShiplifyForAddressBookStops === true;

  const runQuote = useCallback(
    async ({
      formValues,
      buildRequest,
      signal,
      onShiplifyChecked,
      onJobStarted,
    }: RunLTLQuoteParams): Promise<LTLQuoteJobOutcome> => {
      let checkedValues = formValues;
      if (shouldCheckShiplify) {
        try {
          checkedValues = await applyMissingShiplifyLookups(
            formValues,
            ltlAccessorials,
            {
              requireLocationLineFullAddress: true,
              skipAddressBookStops,
            }
          );
          onShiplifyChecked?.(checkedValues);
        } catch (error) {
          // Rates still go out without Shiplify, as in the single form.
          captureException(error, {
            functionName: 'useLTLQuoteJob.applyMissingShiplifyLookups',
          });
        }
      }

      return runLTLQuoteJob(buildRequest(checkedValues), {
        tms,
        isThreeGActive,
        signal,
        onJobStarted,
      });
    },
    [
      shouldCheckShiplify,
      skipAddressBookStops,
      ltlAccessorials,
      tms,
      isThreeGActive,
    ]
  );

  const resumeQuote = useCallback(
    (pointer: LTLQuoteJobPointer, signal: AbortSignal) =>
      pollLTLQuoteJob(pointer, {
        tms,
        isThreeGActive,
        signal,
        pollImmediately: true,
      }),
    [tms, isThreeGActive]
  );

  return { runQuote, resumeQuote };
}
